import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface TrainingJob {
  id: string
  model: string
  status: string
  progress: number
  gpu: string
  started: string
  eta: string
}

interface Resource {
  resource: string
  total: number
  in_use: number
  utilization: number
}

interface Statistics {
  active_jobs: number
  completed_today: number
  completed_mtd: number
  avg_training_time_min: number
  gpu_utilization: number
  success_rate: number
}

export default function ModelTrainingPage() {
  const [jobs, setJobs] = useState<TrainingJob[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [jobsRes, resourcesRes, statsRes] = await Promise.all([
        fetch('/api/modeltraining/jobs'),
        fetch('/api/modeltraining/resources'),
        fetch('/api/modeltraining/statistics')
      ])
      if (jobsRes.ok) setJobs(await jobsRes.json())
      if (resourcesRes.ok) setResources(await resourcesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching model training:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Running') return 'bg-blue-100 text-blue-800'
    if (status === 'Completed') return 'bg-green-100 text-green-800'
    if (status === 'Failed') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Model Training</h1>
          <p className="mt-2 text-gray-600">Phase 272 - Training jobs and hyperparameter tuning</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Jobs</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_jobs}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Completed Today</p>
              <p className="text-2xl font-bold text-green-600">{statistics.completed_today}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Completed MTD</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.completed_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Time</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_training_time_min}m</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">GPU Utilization</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.gpu_utilization}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.success_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Resource Usage</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {resources.map((res, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{res.resource}</span>
                  <span className="text-lg font-bold text-blue-600">{res.utilization}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-2">
                  <div className={`h-2 rounded-full ${res.utilization >= 80 ? 'bg-red-500' : res.utilization >= 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: `${res.utilization}%`}}></div>
                </div>
                <p className="text-sm text-gray-500">{res.in_use} / {res.total} in use</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Training Jobs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GPU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ETA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{job.model}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(job.status)}`}>{job.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{width: `${job.progress}%`}}></div>
                      </div>
                      <span className="text-sm text-gray-500">{job.progress}%</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{job.gpu}</td>
                    <td className="px-6 py-4 text-gray-500">{job.started}</td>
                    <td className="px-6 py-4 text-gray-500">{job.eta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
